import {
  faCircleCheck,
  faCircleEllipsis,
  faCircleHalfStroke,
  faCircleMinus,
} from '@fortawesome/pro-solid-svg-icons';
import { times } from 'lodash';
import {
  CreateTrainingPlanInput,
  TrainingPlanBasicsFragment,
  TrainingPlanBlockDetailsFragment,
  TrainingPlanBlockInput,
  TrainingPlanDetailsFragment,
  TrainingPlanExerciseDetailsFragment,
  TrainingPlanExerciseInput,
  TrainingPlanSetDetailsFragment,
  TrainingPlanSetInput,
  TrainingPlanWeekDetailsFragment,
  TrainingPlanWeekInput,
  TrainingPlanWorkoutDetailsFragment,
  TrainingPlanWorkoutInput,
} from '../../../graphql/types';
import { BLUE200, BRIGHTBLUE200, GREEN200, ORANGE200 } from '../../../theme';
import { shortDateYear } from '../../../utils/date';

const convertTemplateExerciseToPlanInputExercise = (
  exercise: TrainingPlanExerciseDetailsFragment,
): TrainingPlanExerciseInput => ({
  order: exercise.order,
  count: parseInt(exercise.count.toString()),
  unit: exercise.unit.value,
  exerciseId: exercise.exercise.id,
});

const convertTemplateSetToPlanInputSet = (
  set: TrainingPlanSetDetailsFragment,
): TrainingPlanSetInput => ({
  order: set.order,
  trainingPlanExercises: set.trainingPlanExercises.edges.map(
    ({ node: exercise }) =>
      convertTemplateExerciseToPlanInputExercise(exercise),
  ),
});

const convertTemplateBlockToPlanInputBlock = (
  block: TrainingPlanBlockDetailsFragment,
): TrainingPlanBlockInput => ({
  order: block.order,
  trainingComponent: block.trainingComponent.value,
  sets: times(block.sets.count, (index) =>
    convertTemplateSetToPlanInputSet({
      ...block.sets.edges[0].node,
      order: index,
    }),
  ),
});

const convertTemplateWorkoutToPlanInputWorkout = (
  workout: TrainingPlanWorkoutDetailsFragment,
): TrainingPlanWorkoutInput => ({
  id: undefined,
  order: workout.order,
  blocks: workout.blocks.edges.map(({ node: block }) =>
    convertTemplateBlockToPlanInputBlock(block),
  ),
});

const convertTemplateWeekToPlanInputWeek = (
  week: TrainingPlanWeekDetailsFragment,
): TrainingPlanWeekInput => ({
  order: week.order,
  workouts: week.workouts.edges.map(({ node: workout }) =>
    convertTemplateWorkoutToPlanInputWorkout(workout),
  ),
});

export const convertTemplateToPlanInput = (
  template: TrainingPlanDetailsFragment,
  additionalInput: Pick<
    CreateTrainingPlanInput,
    'coachId' | 'userId' | 'templateId' | 'name'
  >,
): CreateTrainingPlanInput => {
  const planInput = {
    ...additionalInput,
    difficulty: template.difficulty,
    frequency: template.frequency,
    durationMinutes: template.durationMinutes,
    id: undefined,
    __typename: undefined,
    createdAt: undefined,
    completedAt: undefined,
    archivedAt: undefined,
    weeks: template.weeks.edges.map(({ node }) =>
      convertTemplateWeekToPlanInputWeek(node),
    ),
  };
  return planInput;
};

export const getPlanIndicator = (
  plan: TrainingPlanBasicsFragment | TrainingPlanDetailsFragment,
  isCurrentPlan: boolean,
) => {
  // default: queued case
  let indicatorIcon = faCircleEllipsis;
  let indicatorColor = ORANGE200;
  let indicatorText = 'Queued';
  let dateFormatted = shortDateYear(plan.createdAt);
  let dateText = `Published ${dateFormatted}`;

  if (plan.archivedAt) {
    indicatorIcon = faCircleMinus;
    indicatorColor = BLUE200;
    indicatorText = 'Archived';
    dateFormatted = shortDateYear(plan.archivedAt);
    dateText = `Archived ${dateFormatted}`;
  } else if (plan.completedAt) {
    indicatorIcon = faCircleCheck;
    indicatorColor = GREEN200;
    indicatorText = 'Completed';
    dateFormatted = shortDateYear(plan.completedAt);
    dateText = `Completed ${dateFormatted}`;
  } else if (isCurrentPlan) {
    indicatorColor = BRIGHTBLUE200;
    indicatorIcon = faCircleHalfStroke;
    indicatorText = 'Active';
  }

  return {
    indicatorIcon,
    indicatorColor,
    indicatorText,
    dateText,
  };
};
